import React from "react";
import { arrayOf, func, shape, string } from "prop-types";
import {
  Card,
  CardMedia,
  CardContent,
  Typography,
  Stack,
  Container,
  Box,
  Button,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import ModalWithLauncher from "../modals/ModalWithLauncher";
import Survey from "./Survey";
import ConnectSVG from "shared/styles/landing/im-connect-to-ec.svg";
import CatchSVG from "shared/styles/landing/ec-catch-meeting-highlights.svg";
import CompareSVG from "shared/styles/landing/ec-compare-pricing.svg";
import SearchSVG from "shared/styles/landing/ec-no-more-hunting.svg";
import DirectSVG from "shared/styles/landing/im-direct-from-icer.svg";
import NewSVG from "shared/styles/landing/im-stay-in-the-know.svg";
import IMImage from "shared/styles/landing/im-preview1020x660.png";
import ECImage from "shared/styles/landing/ec-preview1020x660.png";
import { appPageTitleStyle } from "../AppPages";
import googleAnalytics from "shared/analytics";

const toolDescriptions = [
  {
    name: "Evidence Compendium",
    description: (className) => (
      <Typography variant="body1" className={className}>
        Streamline the use of ICER Analyses so you can make the <strong>best decisions</strong>.
      </Typography>
    ),
    image: ECImage,
    points: [
      {
        title: "No more hunting",
        icon: SearchSVG,
        description: (className) => (
          <Typography variant="body1" className={className}>
            You can search for ICER reviewed drugs by: <em>Generic Name</em>,<em>Trade Name</em>,{" "}
            <em>Condition</em>.
          </Typography>
        ),
      },
      {
        title: "Compare pricing",
        icon: CompareSVG,
        description: (className) => (
          <Typography variant="body1" className={className}>
            Fast and interactive data visualization to compare how your price ranks against the ICER
            prices.
          </Typography>
        ),
      },
      {
        title: "Catch the highlights",
        icon: CatchSVG,
        description: (className) => (
          <Typography variant="body1" className={className}>
            Report and meeting highlights captured in a brief video, along with the Report at a
            Glance, Patient Perspectives, and key evidence summaries and meeting snapshots.
          </Typography>
        ),
      },
    ],
  },
  {
    name: "Interactive Modeler",
    description: (className) => (
      <Typography className={className}>
        Share <strong>your assumptions</strong> based on new evidence using ICER’s models.
      </Typography>
    ),
    image: IMImage,
    points: [
      {
        title: "Direct from ICER to you",
        icon: DirectSVG,
        description: (className) => (
          <Typography variant="body1" className={className}>
            Work directly with ICER’s models by varying inputs and estimates.
          </Typography>
        ),
      },
      {
        title: "Connect to the Evidence Compendium",
        icon: ConnectSVG,
        description: (className) => (
          <Typography variant="body1" className={className}>
            Publish your alternative pricing benchmarks to all of the tool&apos;s users.
          </Typography>
        ),
      },
      {
        title: "Stay in the Know",
        icon: NewSVG,
        description: (className) => (
          <Typography variant="body1" className={className}>
            Your subscription allows you rapid access to the new models as ICER releases them.
          </Typography>
        ),
      },
    ],
  },
];

const LandingTools = () => {
  const tools = () => {
    return toolDescriptions.map((tool, i) => (
      <Box
        key={i}
        sx={(theme) => ({
          display: "flex",
          flex: 1,
          marginBottom: theme.spacing(4),
          [theme.breakpoints.up("sm")]: {
            marginBottom: 0,
          },
        })}
      >
        <Box mb={4} sx={{ flex: "1 1 100%" }}>
          <ToolContent tool={tool} />
        </Box>
      </Box>
    ));
  };

  const ModalLauncher = () => (
    <Button
      color="primary"
      onClick={() => googleAnalytics.trackTrialRequest("under tool cards")}
      sx={(theme) => ({
        width: "100%",
        padding: "15px",
        background: theme.palette.primary.main,
        fontSize: 32,
        color: "white",
        fontWeight: 700,
        "&:hover,&:focus": {
          background: theme.palette.secondary.main,
        },
      })}
    >
      Request Free Trial
    </Button>
  );

  return (
    <Box
      id="tools"
      component="section"
      sx={{
        position: "relative",
        zIndex: 10,
        marginBottom: "100px",
      }}
    >
      <Typography
        variant="h2"
        component="h2"
        align="center"
        style={{ color: "white", marginBottom: 32 }}
        sx={(theme) => appPageTitleStyle(theme.breakpoints)}
      >
        ICER Analytics: two tools in one
      </Typography>
      <Container>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          sx={(theme) => ({
            marginBottom: theme.spacing(6),
          })}
        >
          {tools()}
        </Stack>
        <ModalWithLauncher
          i18nKey="surveyTrial"
          Content={Survey}
          Launcher={ModalLauncher}
          mx={false}
        />
      </Container>
    </Box>
  );
};

const ToolContent = ({ tool }) => {
  const renderPoint = ({ title, icon, description }, key) => (
    <ListItem
      key={key}
      sx={(theme) => ({
        marginBottom: theme.spacing(2),
        paddingBottom: theme.spacing(3),
        borderBottom: "2px solid #F4F4F4",
        justifyContent: "space-between",
        "&:last-of-type": {
          borderBottom: 0,
          margin: 0,
        },
      })}
    >
      <Box
        sx={(theme) => ({
          flex: "1 1 75px",
          minWidth: "75px",
          maxWidth: "75px",
          display: "flex",
          justifyContent: "center",
          marginRight: theme.spacing(3),
        })}
      >
        <img
          src={icon}
          data-object-fit="cover"
          style={{ width: "100%", flex: "1 1 100%" }}
          alt=""
        />
      </Box>
      <ListItemText
        primary={title}
        secondary={description()}
        sx={{
          "& .MuiListItemText-primary": {
            fontSize: 20,
            fontWeight: "bold",
            lineHeight: "26px",
          },
          "& .MuiListItemText-secondary": {
            fontSize: 18,
            fontWeight: 300,
            lineHeight: "22px",
          },
        }}
      />
    </ListItem>
  );
  return (
    <Card
      title={tool.title}
      sx={{
        background: "#FFFFFF 0% 0% no-repeat padding-box",
        boxShadow: "0px 3px 20px #00000029",
        border: "10px solid #FFFFFF80",
        borderRadius: "20px",
        minHeight: "100%",
      }}
    >
      <CardContent
        sx={(theme) => ({
          padding: theme.spacing(2),
          [theme.breakpoints.up("sm")]: {
            padding: theme.spacing(3),
          },
          [theme.breakpoints.up("md")]: {
            padding: theme.spacing(4),
          },
        })}
      >
        <Typography
          variant="h3"
          sx={(theme) => ({
            color: theme.palette.primary.main,
            textTransform: "uppercase",
            font: "normal normal bold 35px/70px Lato",
            lineHeight: 1,
            marginBottom: theme.spacing(1),
          })}
        >
          {tool.name}
        </Typography>
        {tool.description((theme) => ({
          marginBottom: theme.spacing(3),
          "& strong": {
            fontWeight: 600,
          },
        }))}
        <Box sx={{ minHeight: "250px", color: "#999" }}>
          <CardMedia image={tool.image} title={tool.title} component="img" />
        </Box>
        <List>{tool.points && tool.points.map((p, i) => renderPoint(p, i))}</List>
      </CardContent>
    </Card>
  );
};

ToolContent.propTypes = {
  tool: shape({
    title: string,
    name: string.isRequired,
    description: func.isRequired,
    image: string.isRequired,
    points: arrayOf(
      shape({
        title: string.isRequired,
        icon: string.isRequired,
        description: func.isRequired,
      }),
    ),
  }).isRequired,
};

export default LandingTools;
